import React, { useContext } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography
} from '@mui/material';
import { ChatContext } from '../../contexts/ChatContext';
import OnlineStatusDot from './OnlineStatusDot';

const ConversationList = () => {
  const {
    conversations,
    setActiveConversation,
    unreadCounts,
    getUserStatus
  } = useContext(ChatContext);

  const formatLastMessage = (message) => {
    if (!message) return 'Noch keine Nachrichten';
    return message.content.length > 50
      ? message.content.substring(0, 50) + '...'
      : message.content;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Jetzt';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  if (conversations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Keine Conversations vorhanden.
          <br />
          Starte ein neues Gespräch über "Users".
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', p: 0 }}>
      {conversations.map((conv) => {
        const otherUser = conv.other_user;
        const unreadCount = unreadCounts[conv.id] || 0;
        const status = getUserStatus(otherUser.id);

        return (
          <ListItem key={conv.id} disablePadding>
            <ListItemButton onClick={() => setActiveConversation(conv)}>
              <ListItemAvatar>
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  overlap="circular"
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {(otherUser.full_name || otherUser.username).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                      <OnlineStatusDot
                        status={status}
                        lastSeenAt={otherUser.last_seen_at}
                        size={12}
                      />
                    </Box>
                  </Box>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: unreadCount > 0 ? 600 : 400 }}
                    >
                      {otherUser.full_name || otherUser.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(conv.last_message_at)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: unreadCount > 0 ? 500 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatLastMessage(conv.last_message)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

export default ConversationList;
